/**
 * fmedaEngine.js
 * Failure Modes, Effects and Criticality Analysis with Diagnostic Coverage (FMEDA)
 * Implements FMEDA per IEC 61508-2 Annex C for SIL allocation
 *
 * REFERENCES:
 *   - IEC 61508-2:2010 Requirements for electrical/e/pe safety-related systems, Annex C
 *   - IEC 61511-1:2016 Functional safety - Safety instrumented systems for the process industry
 *   - Birolini, A. (2014) "Reliability Engineering: Theory and Practice" 5th Ed.
 *   - OREDA 6th Edition (2015) Offshore Reliability Data
 *
 * OVERVIEW:
 *   FMEDA provides systematic analysis of failures at the component level, computing
 *   Safe Failure Fraction (SFF) and Diagnostic Coverage (DC) for SIL determination.
 *   Key metrics: λ_safe, λ_dangerous, λ_DD (dangerous detected), λ_DU (dangerous undetected).
 */

import { ALTEngine } from './altEngine.js';

export class FMEDAEngine {
  /**
   * Constructor
   * @param {Array} components - Array of component definitions
   */
  constructor(components = []) {
    this.components = components;
    this.analysisResults = null;
    this.silAllocation = null;
  }

  /**
   * Main FMEDA analysis per IEC 61508-2 Annex C
   * Computes λ_safe, λ_dangerous, λ_DD, λ_DU, and SFF
   *
   * @param {Array} components - Each component: {id, λ, type: 'safety'|'control', diagnosticCoverage, failureModes}
   *   where failureModes = [{mode, severity, dc}], severity in {safe, dangerous}, dc in [0,1]
   * @returns {Object} {lambdaSafe, lambdaDangerous, lambdaDD, lambdaDU, sff, allocableSil, components}
   */
  analyze(components) {
    if (!components || components.length === 0) {
      throw new Error('FMEDA.analyze: no components provided');
    }

    let totalLambdaSafe = 0;
    let totalLambdaDangerous = 0;
    let totalLambdaDD = 0;
    let totalLambdaDU = 0;

    const componentResults = [];

    for (const comp of components) {
      if (!comp.id || comp.λ === undefined) {
        throw new Error('Each component must have id and λ (failure rate)');
      }

      const lambda = comp.λ;
      const dc = comp.diagnosticCoverage || 0;
      const failureModes = comp.failureModes || [];

      let compLambdaSafe = 0;
      let compLambdaDangerous = 0;
      let compLambdaDD = 0;
      let compLambdaDU = 0;

      // Process each failure mode per IEC 61508-2 Annex C
      if (failureModes.length === 0) {
        // No explicit modes: assume all failures are dangerous undetected
        compLambdaDangerous = lambda;
        compLambdaDU = lambda * (1 - dc);
        compLambdaDD = lambda * dc;
      } else {
        // Allocate lambda across modes
        let totalModeWeight = failureModes.length;
        const lambdaPerMode = lambda / totalModeWeight;

        for (const mode of failureModes) {
          const modeDC = mode.dc !== undefined ? mode.dc : dc;
          const severity = mode.severity || 'dangerous';

          if (severity === 'safe') {
            compLambdaSafe += lambdaPerMode;
          } else {
            // dangerous
            compLambdaDangerous += lambdaPerMode;
            compLambdaDD += lambdaPerMode * modeDC;
            compLambdaDU += lambdaPerMode * (1 - modeDC);
          }
        }
      }

      totalLambdaSafe += compLambdaSafe;
      totalLambdaDangerous += compLambdaDangerous;
      totalLambdaDD += compLambdaDD;
      totalLambdaDU += compLambdaDU;

      componentResults.push({
        id: comp.id,
        lambda: lambda,
        type: comp.type || 'control',
        lambdaSafe: compLambdaSafe,
        lambdaDangerous: compLambdaDangerous,
        lambdaDD: compLambdaDD,
        lambdaDU: compLambdaDU,
        diagnosticCoverage: dc,
      });
    }

    // Safe Failure Fraction per IEC 61508-2 Annex C
    // SFF = (λ_safe + λ_DD) / (λ_safe + λ_dangerous)
    const totalLambda = totalLambdaSafe + totalLambdaDangerous;
    const sff =
      totalLambda > 0 ? (totalLambdaSafe + totalLambdaDD) / totalLambda : 0;

    // Determine allocable SIL per IEC 61508-2 Table 2 & 3
    // Simplified: assume HFT=0 (no redundancy), Type A components
    const allocableSil = this._determineSilFromSff(sff, 0, 'A');

    this.analysisResults = {
      lambdaSafe: totalLambdaSafe,
      lambdaDangerous: totalLambdaDangerous,
      lambdaDD: totalLambdaDD,
      lambdaDU: totalLambdaDU,
      totalLambda: totalLambda,
      sff: sff,
      allocableSil: allocableSil,
      componentResults: componentResults,
      analysisStandard: 'IEC 61508-2:2010 Annex C',
    };

    return this.analysisResults;
  }

  /**
   * Determine SIL from SFF per IEC 61508-2 Table 2 (Type A) and Table 3 (Type B)
   * Simplified mapping; full HFT table in SIL Engine
   *
   * @param {number} sff - Safe Failure Fraction [0,1]
   * @param {number} hft - Hardware Fault Tolerance (default 0)
   * @param {string} type - Type 'A' or 'B'
   * @returns {Object} {sil, constraints}
   */
  _determineSilFromSff(sff, hft = 0, type = 'A') {
    let maxSil = null;

    if (type === 'A') {
      if (hft === 0) {
        if (sff < 0.6) maxSil = 1;
        else if (sff < 0.9) maxSil = 2;
        else maxSil = 3;
      } else if (hft === 1) {
        maxSil = sff >= 0.6 ? 3 : 2;
      } else if (hft >= 2) {
        maxSil = sff >= 0.6 ? 4 : 3;
      }
    } else if (type === 'B') {
      if (hft === 0) {
        maxSil = sff >= 0.9 ? 3 : 2;
      } else if (hft === 1) {
        maxSil = sff >= 0.9 ? 4 : 3;
      }
    }

    return {
      sil: maxSil,
      hft: hft,
      type: type,
      sff: sff.toFixed(4),
      constraints: `Type ${type}, HFT=${hft}`,
    };
  }

  /**
   * Sum failure rates across all components
   * @param {Array} componentList - Array of components with λ
   * @returns {number} Total failure rate
   */
  partsCount(componentList) {
    if (!componentList || !Array.isArray(componentList)) {
      return 0;
    }

    let totalLambda = 0;
    for (const comp of componentList) {
      if (comp.λ !== undefined) {
        totalLambda += comp.λ;
      }
    }

    return totalLambda;
  }

  /**
   * Extended FMECA (Criticality Analysis) with risk matrix
   * @param {Array} components - Component array with criticality estimates
   * @returns {Object} Criticality assessment
   */
  fmecaAnalysis(components) {
    const fmeda = this.analyze(components);

    // Criticality index: (λ × severity) / total
    const criticalComponents = [];

    for (const compRes of fmeda.componentResults) {
      const criticality =
        (compRes.lambdaDangerous / fmeda.totalLambda) * 100;
      if (criticality > 5) {
        // Flag items > 5% of dangerous lambda
        criticalComponents.push({
          id: compRes.id,
          criticality: criticality.toFixed(2),
          lambdaDangerous: compRes.lambdaDangerous,
          recommendation:
            criticality > 20
              ? 'Urgent: consider design change'
              : 'Review failure mode mitigation',
        });
      }
    }

    return {
      fmedaResults: fmeda,
      criticalComponents: criticalComponents,
      topRisk:
        criticalComponents.length > 0 ? criticalComponents[0].id : 'None',
    };
  }

  /**
   * Get summary report
   * @returns {Object} Analysis summary
   */
  getSummary() {
    if (!this.analysisResults) {
      throw new Error('No analysis results. Call analyze() first.');
    }

    const res = this.analysisResults;
    return {
      totalLambda: res.totalLambda.toFixed(8),
      lambdaSafe: res.lambdaSafe.toFixed(8),
      lambdaDangerous: res.lambdaDangerous.toFixed(8),
      lambdaDD: res.lambdaDD.toFixed(8),
      lambdaDU: res.lambdaDU.toFixed(8),
      sff: (res.sff * 100).toFixed(2) + '%',
      allocableSil: res.allocableSil.sil,
      componentCount: res.componentResults.length,
      standard: res.analysisStandard,
    };
  }
}

export default FMEDAEngine;
